import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import {api, API_BASE, getCookie} from '../../api';


export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [records, setRecords] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const loadRecords = () => api(`/vehicles/${id}/service-records`).then(r => r.json()).then(setRecords).catch(() => {});

  useEffect(() => {
    api(`/vehicles/${id}`).then(r => r.json()).then(setVehicle);
    loadRecords();
  }, [id]);

  const loadAttachments = (recordId) => api(`/vehicles/${id}/service-records/${recordId}/attachments`).then(r => r.ok ? r.json() : []).then(setAttachments);
  const toggleRecord = (recordId) => {
    if (expandedRecord === recordId) { setExpandedRecord(null); setAttachments([]); }
    else { setExpandedRecord(recordId); loadAttachments(recordId); }
  };
  const handleUpload = async (recordId, e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData(); formData.append('file', file);
    const jwtToken = getCookie('jwtToken'); const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    const tid = getCookie('tenantId'); if (tid) headers['X-Tenant-Id'] = tid;
    await fetch(`${API_BASE}/vehicles/${id}/service-records/${recordId}/attachments`, {method: 'POST', headers, body: formData});
    if (fileRef.current) fileRef.current.value = ''; setUploading(false); loadAttachments(recordId);
  };
  const isPdf = (ct) => ct === 'application/pdf';
  const isImage = (ct) => ct?.startsWith('image/');
  const isPreviewable = (ct) => isPdf(ct) || isImage(ct);
  const handlePreview = async (recordId, attachment) => {
    const res = await api(`/vehicles/${id}/service-records/${recordId}/attachments/${attachment.id}`);
    if (!res.ok) return;
    const data = await res.json();
    if (isPreviewable(attachment.contentType)) {
      setPreview({ url: data.url, fileName: attachment.fileName, contentType: attachment.contentType });
    } else {
      const a = document.createElement('a'); a.href = data.url; a.download = attachment.fileName; a.click();
    }
  };
  const closePreview = () => setPreview(null);
  const handleDownloadAttachment = async (recordId, attachmentId, fileName) => {
    const res = await api(`/vehicles/${id}/service-records/${recordId}/attachments/${attachmentId}`);
    if (res.ok) { const data = await res.json(); const a = document.createElement('a'); a.href = data.url; a.download = fileName; a.click(); }
  };
  const handleDeleteAttachment = async (recordId, attachmentId) => {
    await api(`/vehicles/${id}/service-records/${recordId}/attachments/${attachmentId}`, {method: 'DELETE'}); loadAttachments(recordId);
  };

  const confirmDelete = (title, message, onConfirm) => setDeleteModal({ open: true, title, message, onConfirm });
  const closeModal = () => setDeleteModal({ open: false, title: '', message: '', onConfirm: null });

  const handleDelete = () => confirmDelete('Delete Vehicle', 'Are you sure you want to delete this vehicle? This action cannot be undone.', async () => {
    await api(`/vehicles/${id}`, { method: 'DELETE' });
    navigate('/vehicles');
  });

  const handleDeleteRecord = (recordId) => confirmDelete('Delete Service Record', 'Are you sure you want to delete this service record?', async () => {
    await api(`/vehicles/${id}/service-records/${recordId}`, { method: 'DELETE' });
    closeModal();
    loadRecords();
  });

  if (!vehicle) return null;

  return (
    <Layout>
      <Link to="/vehicles" className="text-purple-600 hover:text-purple-800 mb-4 inline-block">&larr; Back to Vehicles</Link>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{vehicle.year} {vehicle.make} {vehicle.model}</h1>
              {vehicle.vin && <p className="text-gray-600 font-mono mt-1">{vehicle.vin}</p>}
            </div>
            <div className="flex items-center gap-3">
              {vehicle.status && (
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  vehicle.status === 'Active' ? 'bg-green-100 text-green-800' :
                  vehicle.status === 'In Service' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {vehicle.status}
                </span>
              )}
              <Link to={`/vehicles/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={handleDelete} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Color</p>
            <p className="text-gray-900 font-medium">{vehicle.color || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Mileage</p>
            <p className="text-gray-900 font-medium">{vehicle.mileage?.toLocaleString() || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">License Plate</p>
            <p className="text-gray-900 font-medium">{[vehicle.licensePlate, vehicle.licensePlateState].filter(Boolean).join(' · ') || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Added</p>
            <p className="text-gray-900 font-medium">{new Date(vehicle.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">Service History</h2>
          <Link to={`/vehicles/${id}/service/new`} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-center">Add Record</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mileage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shop</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleRecord(r.id)}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      <span className="mr-2 text-gray-400">{expandedRecord === r.id ? '▾' : '▸'}</span>{r.serviceType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{r.serviceDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{r.mileage?.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{r.cost != null ? `$${r.cost.toFixed(2)}` : '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{r.shop || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        r.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        r.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                        r.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {r.status || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-3" onClick={e => e.stopPropagation()}>
                        <Link to={`/vehicles/${id}/service/${r.id}/edit`} className="text-purple-600 hover:text-purple-800 font-medium">Edit</Link>
                        <button onClick={() => handleDeleteRecord(r.id)} className="text-purple-600 hover:text-purple-800 font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                  {expandedRecord === r.id && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-700">Attachments</h4>
                          <label className={`px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                            {uploading ? 'Uploading...' : '+ Upload'}
                            <input ref={fileRef} type="file" className="hidden" onChange={(e) => handleUpload(r.id, e)} disabled={uploading} />
                          </label>
                        </div>
                        {attachments.length === 0 && <p className="text-gray-400 text-sm">No attachments</p>}
                        <div className="space-y-2">{attachments.map(a => (
                          <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                            <button onClick={() => handlePreview(r.id, a)} className="text-sm text-purple-600 hover:text-purple-800 truncate flex-1 text-left">{a.fileName}</button>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              <span className="text-xs text-gray-400">{a.size ? (a.size < 1024*1024 ? (a.size/1024).toFixed(0)+'KB' : (a.size/1024/1024).toFixed(1)+'MB') : ''}</span>
                              {isPreviewable(a.contentType) && <button onClick={() => handleDownloadAttachment(r.id, a.id, a.fileName)} className="text-gray-400 hover:text-gray-600 text-xs">↓</button>}
                              <button onClick={() => handleDeleteAttachment(r.id, a.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                            </div>
                          </div>
                        ))}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {records.length === 0 && (
            <div className="text-center py-12 text-gray-500">No service records yet.</div>
          )}
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closePreview}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{preview.fileName}</h3>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="flex-1 overflow-hidden">
              {isPdf(preview.contentType) && <iframe src={preview.url} className="w-full h-full border-0" title={preview.fileName} />}
              {isImage(preview.contentType) && <div className="w-full h-full flex items-center justify-center p-4 bg-gray-50"><img src={preview.url} alt={preview.fileName} className="max-w-full max-h-full object-contain" /></div>}
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={deleteModal.open}
        title={deleteModal.title}
        message={deleteModal.message}
        onConfirm={deleteModal.onConfirm}
        onCancel={closeModal}
      />
    </Layout>
  );
}
